import { parseSignInIdentifier, type SignInCodeChannel, type SignInIdentifier } from '@chukua/common';
import { useState } from 'react';
import { sendSignInCode, verifySignInCode, type CurrentUser } from '../../../shared/features/session/session.api';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { RESEND_INTERVAL_SECONDS, retryAfterSeconds } from '../policies/resend-countdown.policy';
import { otherSignInChannels, signInChannelFor } from '../policies/sign-in-channel.policy';
import { sendHint } from '../policies/sign-in-code-copy.policy';
import { useResendCountdown } from './use-resend-countdown';

/** The code last sent: what was typed (the server reads it the same way on verify), how it read, and the channel. */
export interface SentSignInCode {
  typed: string;
  identifier: SignInIdentifier;
  channel: SignInCodeChannel;
  /** The code itself, on a test deployment that shows codes on screen. */
  displayedCode: string | null;
}

interface SignInCodeOptions {
  channels: readonly SignInCodeChannel[];
  onCodeSent: () => void;
  onSignedIn: (user: CurrentUser) => void;
}

/**
 * Signing in with a code to the email or mobile typed. The server answers a send alike whether or not anyone has
 * that email or number, so the code step follows every accepted send, and a correct code is the whole sign-in.
 */
export function useSignInCode({ channels, onCodeSent, onSignedIn }: SignInCodeOptions) {
  const countdown = useResendCountdown();
  const [typed, setTyped] = useState('');
  const [sent, setSent] = useState<SentSignInCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const typedIdentifier = parseSignInIdentifier(typed);
  const typedChannel = typedIdentifier && signInChannelFor(typedIdentifier.kind, channels);

  /** True once the code went; a refusal is shown inline, and a throttle restarts the countdown at the server's wait. */
  async function send(to: string, channel: SignInCodeChannel): Promise<boolean> {
    const identifier = parseSignInIdentifier(to);
    if (!identifier) return false;
    setError(null);
    setSubmitting(true);
    try {
      const answer = await sendSignInCode({ identifier: to, channel });
      setSent({ typed: to, identifier, channel, displayedCode: answer.displayedCode });
      countdown.start(RESEND_INTERVAL_SECONDS);
      return true;
    } catch (err) {
      const wait = retryAfterSeconds(err);
      if (wait) countdown.start(wait);
      setError(apiErrorMessage(err) ?? "Couldn't send the code. Try again.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  async function sendFirstCode(to: string) {
    const identifier = parseSignInIdentifier(to);
    const channel = identifier && signInChannelFor(identifier.kind, channels);
    if (channel && (await send(to, channel))) onCodeSent();
  }

  async function verify(code: string) {
    if (!sent) return;
    setError(null);
    setSubmitting(true);
    try {
      onSignedIn(await verifySignInCode({ identifier: sent.typed, code: code.trim() }));
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'That code is not right.');
    } finally {
      setSubmitting(false);
    }
  }

  return {
    typed,
    setTyped,
    hint: sendHint(typedIdentifier, typedChannel),
    sent,
    otherChannels: sent ? otherSignInChannels(channels, sent.channel) : [],
    error,
    submitting,
    secondsLeft: countdown.secondsLeft,
    sendFirstCode,
    verify,
    /** "Send another code" and "Send it by … instead": the same email or number as the last code. */
    resendBy: (channel: SignInCodeChannel) => sent && void send(sent.typed, channel),
    clearError: () => setError(null),
  };
}

export type SignInCode = ReturnType<typeof useSignInCode>;
